import { type FC, useMemo, useState } from 'react';
import { Check } from 'lucide-react';
import { SearchInput } from '@/acc-ui/primitives/SearchInput';
import { HighlightText } from '@/acc-ui/primitives/HighlightText';
import { SectionLabel } from '@/acc-ui/primitives/Layout';
import { cn } from '@/lib/utils';
import type { Permission } from '@/api/access-api';

/**
 * PermissionPicker — grouped, searchable multi-select for permissions.
 *
 * Shows permissions grouped by their module. Each group has a header with
 * a Select-all / Clear-all affordance. Permission rows show the action +
 * description so the admin understands what they are granting.
 *
 * Designed for use inside the role editor drawer. Controlled component —
 * the parent owns the selected keys set.
 */

export interface PermissionPickerProps {
  permissions: Permission[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  disabled?: boolean;
  className?: string;
}

export const PermissionPicker: FC<PermissionPickerProps> = ({
  permissions,
  selected,
  onChange,
  disabled = false,
  className,
}) => {
  const [search, setSearch] = useState('');

  const groupedFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const match = (p: Permission) =>
      !q ||
      p.key.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q) ||
      p.module.toLowerCase().includes(q);

    const grouped = new Map<string, Permission[]>();
    for (const p of permissions) {
      if (!match(p)) continue;
      const list = grouped.get(p.module) ?? [];
      list.push(p);
      grouped.set(p.module, list);
    }
    return Array.from(grouped.entries())
      .map(([m, ps]) => [m, ps.sort((a, b) => a.action.localeCompare(b.action))] as const)
      .sort(([a], [b]) => a.localeCompare(b));
  }, [permissions, search]);

  const toggleOne = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  };
  const selectModule = (moduleName: string) => {
    const next = new Set(selected);
    for (const p of permissions) if (p.module === moduleName) next.add(p.key);
    onChange(next);
  };
  const clearModule = (moduleName: string) => {
    const next = new Set(selected);
    for (const p of permissions) if (p.module === moduleName) next.delete(p.key);
    onChange(next);
  };

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <SearchInput
        placeholder="Filter permissions…"
        value={search}
        onChange={setSearch}
        shortcut=""
      />

      <div className="flex flex-col gap-4 max-h-[60vh] overflow-y-auto scrollbar-subtle pr-1">
        {groupedFiltered.length === 0 && (
          <div className="text-label text-text-tertiary text-center py-6">
            No permissions match “{search}”.
          </div>
        )}
        {groupedFiltered.map(([moduleName, perms]) => {
          const allSelected = perms.every((p) => selected.has(p.key));
          const someSelected = perms.some((p) => selected.has(p.key));
          return (
            <div key={moduleName} className="data-surface">
              <div className="flex items-center justify-between px-3 py-2 border-b border-glass-hairline">
                <SectionLabel>
                  {moduleName}
                  <span className="ml-2 normal-case text-text-tertiary font-medium">
                    ({perms.filter((p) => selected.has(p.key)).length}/{perms.length})
                  </span>
                </SectionLabel>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      allSelected ? clearModule(moduleName) : selectModule(moduleName)
                    }
                    disabled={disabled}
                    className="text-micro font-medium text-brand-blue hover:underline disabled:opacity-50"
                  >
                    {allSelected ? 'Clear module' : someSelected ? 'Select all' : 'Select all'}
                  </button>
                </div>
              </div>
              <ul className="divide-y divide-glass-hairline">
                {perms.map((p) => {
                  const isOn = selected.has(p.key);
                  return (
                    <li key={p.key}>
                      <label
                        className={cn(
                          'flex items-start gap-3 px-3 py-2 cursor-pointer',
                          disabled && 'cursor-not-allowed opacity-60',
                          !disabled && 'hover:bg-brand-blue/[0.04]',
                        )}
                      >
                        <span
                          className={cn(
                            'mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors',
                            isOn
                              ? 'bg-brand-blue border-brand-blue text-white'
                              : 'bg-white border-slate-300',
                          )}
                          aria-hidden
                        >
                          {isOn && <Check className="w-3 h-3" />}
                        </span>
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={isOn}
                          onChange={() => toggleOne(p.key)}
                          disabled={disabled}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-label font-semibold text-text-primary">
                            <HighlightText text={p.action} query={search} />
                            <span className="ml-2 font-mono font-normal text-micro text-text-muted">
                              <HighlightText text={p.key} query={search} />
                            </span>
                          </span>
                          <span className="block text-caption text-text-tertiary mt-0.5">
                            <HighlightText text={p.description} query={search} />
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PermissionPicker;
